// ============================================================
// BANK DETAILS PAGE
// ============================================================

import React, { useState, useEffect } from 'react';
import { CreditCard, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { Table, Badge, Button, Modal, StatCard } from '../components/ui';
import { getBankDetails } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatDate } from '../utils/helpers';
import { toast } from 'react-toastify';

const BankDetails = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState(null);
  const [showAccNo, setShowAccNo] = useState({});

  useEffect(() => {
    adminApi.getBankDetails(getBankDetails)
      .then(res => { setRecords(Array.isArray(res) ? res : (res?.items || [])); setLoading(false); })
      .catch(e => { setError(e.message || 'Failed to load data'); setLoading(false); });
  }, []);

  const verify = async (id, status) => {
    try { await adminApi.verifyBankDetails(id, status); } catch { /* fallback */ }
    setRecords(p => p.map(r => r.id === id ? { ...r, verificationStatus: status } : r));
    toast.success(`Bank details ${status}`);
  };

  const mask = (v) => v ? '*'.repeat(v.length - 4) + v.slice(-4) : '—';

  const columns = [
    { key: 'entityName', label: 'Entity', render: v => <b>{v}</b> },
    { key: 'entityType', label: 'Type', render: v => <Badge status={v} text={v} /> },
    { key: 'bankName', label: 'Bank', render: v => <span><CreditCard size={12} style={{ marginRight: 4 }} />{v}</span> },
    {
      key: 'accountNumber', label: 'Acc. Number',
      render: (v, row) => (
        <span style={{ fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 6 }}>
          {showAccNo[row.id] ? v : mask(v)}
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 0 }}
            onClick={() => setShowAccNo(p => ({ ...p, [row.id]: !p[row.id] }))}>
            {showAccNo[row.id] ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
        </span>
      )
    },
    { key: 'ifscCode', label: 'IFSC', render: v => <code style={{ fontSize: 12 }}>{v}</code> },
    { key: 'verificationStatus', label: 'Verification', render: v => <Badge status={v === 'verified' ? 'active' : v === 'rejected' ? 'rejected' : 'pending'} text={v} /> },
    {
      key: 'id', label: 'Actions',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="secondary" onClick={() => setView(row)}>View</Button>
          {row.verificationStatus === 'pending' && (
            <>
              <Button size="sm" variant="success" icon={<CheckCircle size={12} />} onClick={() => verify(row.id, 'verified')}>Verify</Button>
              <Button size="sm" variant="danger" icon={<XCircle size={12} />} onClick={() => verify(row.id, 'rejected')}>Reject</Button>
            </>
          )}
        </div>
      )
    },
  ];

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Bank Details</h1>
          <p className="admin-page-subtitle">Manage and verify bank account information</p>
        </div>
      </div>

      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        <StatCard label="Total Records" value={records.length} icon={<CreditCard size={18} />} color="#3B82F6" />
        <StatCard label="Verified" value={records.filter(r => r.verificationStatus === 'verified').length} icon={<CheckCircle size={18} />} color="#10B981" />
        <StatCard label="Pending" value={records.filter(r => r.verificationStatus === 'pending').length} icon={<CreditCard size={18} />} color="#F59E0B" />
        <StatCard label="Rejected" value={records.filter(r => r.verificationStatus === 'rejected').length} icon={<XCircle size={18} />} color="#EF4444" />
      </div>

      <div className="admin-table-wrapper">
        <div className="admin-table-toolbar">
          <span style={{ fontSize: 14, fontWeight: 700 }}>{records.length} Bank Accounts</span>
        </div>
        <Table columns={columns} data={records} loading={loading} />
      </div>

      <Modal open={!!view} onClose={() => setView(null)} title="Bank Account Details"
        footer={<Button variant="secondary" onClick={() => setView(null)}>Close</Button>}>
        {view && (
          <div className="admin-info-grid">
            {[
              ['Entity Name', view.entityName], ['Entity Type', view.entityType],
              ['Bank Name', view.bankName], ['Branch', view.branch],
              ['Account Number', view.accountNumber], ['IFSC Code', view.ifscCode],
              ['Account Type', view.accountType], ['Account Holder', view.accountHolder],
              ['Status', view.verificationStatus], ['Added On', formatDate(view.createdAt)],
            ].map(([l, v]) => (
              <div key={l} className="admin-info-item">
                <span className="admin-info-label">{l}</span>
                <span className="admin-info-value">{v}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BankDetails;
